<template>
    <b-card-body>
        <!-- Table Container Card -->
        <b-card no-body class="mb-0" data-test="secretary-table">
            <b-card-header tag="h1" class="border-bottom">
                <h1 class="mb-0">Pending Approval</h1>
            </b-card-header>

            <b-card-body>
                <b-table
                    ref="refChangeSecretaryFormTable"
                    class="position-relative"
                    :items="changeSecretaryFormData"
                    :fields="tableColumnsChangeSecretaryForm"
                    :current-page="currentPageChangeSecretaryForm"
                    :per-page="perPageChangeSecretaryForm"
                    responsive
                    primary-key="id"
                    :sort-by.sync="sortByChangeSecretaryForm"
                    :sort-desc.sync="isSortDirDescChangeSecretaryForm"
                    busy.sync="isBusyChangeSecretaryForm"
                >
                    <template #cell(index)="data" >
                        {{ data.index + 1 }}
                    </template>

                    <template #cell(appointmentDate)="data" >
                        <validation-observer>
                            <validation-provider
                                v-if="data.item.isAppointment"
                                v-slot="{ errors }"
                                name="appointment date"
                                rules="required"
                            >
                                <b-form-group :state="errors.length > 0 ? false : null" style="max-width: 160px;">
                                    <b-input-group class="input-group-merge">
                                        <flat-pickr
                                            v-model="data.item.appointmentDate"
                                            placeholder="Appointment date"
                                            class="form-control"
                                            :config="datePickerConfig"
                                        />

                                        <b-input-group-append is-text>
                                            <feather-icon
                                                class="cursor-pointer"
                                                icon="CalendarIcon"
                                            />
                                        </b-input-group-append>
                                    </b-input-group>
                                    
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <div v-else> {{ data.item.appointmentDate }}</div>
                        </validation-observer>
                    </template>

                    <template #cell(cessationDate)="data" >
                        <validation-observer>
                            <validation-provider
                                v-if="! data.item.isAppointment"
                                v-slot="{ errors }"
                                name="cessation date"
                                rules="required"
                            >
                                <b-form-group :state="errors.length > 0 ? false : null" style="max-width: 160px;">
                                    <b-input-group class="input-group-merge">
                                        <flat-pickr
                                            v-model="data.item.cessationDate"
                                            placeholder="Cessation date"
                                            class="form-control"
                                            :config="datePickerConfig"
                                        />

                                        <b-input-group-append is-text>
                                            <feather-icon
                                                class="cursor-pointer"
                                                icon="CalendarIcon"
                                            />
                                        </b-input-group-append>
                                    </b-input-group>
                                    
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>
                        </validation-observer>
                    </template>

                    <!-- Column: Actions -->
                    <template #cell(actions)= "data">
                        <div class="cursor-pointer">
                            <div v-if="isUserAdmin">
                                <b-dropdown size="sm" menu-class="" dropright variant="link" toggle-class="text-decoration-none" no-caret>
                                    <template #button-content>
                                        <feather-icon icon="MoreVerticalIcon"/>
                                    </template>

                                    <b-dropdown-item
                                        :data-test="`view-${data.item.email}`"
                                        @click="view(data)"
                                    >
                                        <div class="d-flex align-items-center">
                                            <feather-icon icon="EyeIcon" />
                                            <span style="padding-top: .1em; padding-left: .5em">View</span>
                                        </div>
                                    </b-dropdown-item>

                                    <b-dropdown-item
                                        :data-test="`approve-${data.item.email}`"
                                        @click="approve(data)"
                                    >
                                        <div class="d-flex align-items-center">
                                            <feather-icon icon="CheckIcon" />
                                            <span style="padding-top: .1em; padding-left: .5em">Approve</span>
                                        </div>
                                    </b-dropdown-item>
                                </b-dropdown>
                            </div>

                            <div 
                                v-else 
                                :data-test="`view-${data.item.email}`"
                                class="d-flex align-items-center justify-content-end" 
                                @click="view(data)"
                                >
                                <feather-icon icon="EyeIcon" />
                                <span style="padding-top: .1em; padding-left: .5em">View</span>
                            </div>
                        </div>
                    </template>
                </b-table>

                <loader v-if="isLoadingChangeSecretaryForm" data-type="Table" class="custom-loading" />

                <div class="mx-2 mb-2">
                    <b-row>
                        <b-col
                            cols="12"
                            sm="6"
                            class="
                                d-flex
                                align-items-center
                                justify-content-center justify-content-sm-start
                            "
                        >
                            <span class="text-muted"
                                >Showing {{ dataMetaChangeSecretaryForm.from }} to {{ dataMetaChangeSecretaryForm.to }} of
                                {{ dataMetaChangeSecretaryForm.of }} entries</span
                            >
                        </b-col>
                        <!-- Pagination -->
                        <b-col
                            cols="12"
                            sm="6"
                            class="
                                d-flex
                                align-items-center
                                justify-content-center justify-content-sm-end
                            "
                        >
                            <b-pagination
                                v-model="currentPageChangeSecretaryForm"
                                :total-rows="totalSecretariesChangeSecretaryForm"
                                :per-page="perPageChangeSecretaryForm"
                                first-number
                                last-number
                                class="mb-0 mt-1 mt-sm-0"
                                prev-class="prev-item"
                                next-class="next-item"
                            >
                                <template #prev-text>
                                    <feather-icon
                                        icon="ChevronLeftIcon"
                                        size="18"
                                    />
                                </template>
                                <template #next-text>
                                    <feather-icon
                                        icon="ChevronRightIcon"
                                        size="18"
                                    />
                                </template>
                            </b-pagination>
                        </b-col>
                    </b-row>
                </div>
            </b-card-body>
        </b-card>
    </b-card-body>
</template>

<script>
import {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BTable,
    BPagination,
    BDropdown,
    BDropdownItem,
    BFormGroup,
    BInputGroup,
    BInputGroupAppend,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import flatPickr from "vue-flatpickr-component";
import { mapGetters } from "vuex";
import { COMPANY } from "@/constants/config";
import { formatDate } from "@/helpers";

export default {
    components: {
        BRow,
        BCol,
        BCard,
        BTable,
        BDropdown,
        BCardBody,
        BPagination,
        BCardHeader,
        BDropdownItem,
        BFormGroup,
        BInputGroup,
        BInputGroupAppend,

        flatPickr,

        ValidationProvider,
        ValidationObserver,
    },

    props: {
        payloadForChangeSecretaryFormTable: {
            type: Object,
            required: true
        },

        changeSecretaryFormData: {
            type: Array,
            required: true
        }

    },

    data() {
        return {
            formatDate,

            datePickerConfig: {
                altFormat: "j M Y",
                dateFormat: "j M Y",
                disableMobile: "true"
            },
        };
    },

    computed: {
        ...mapGetters({
            isUserAdmin: "auth/isAdmin",
        }),

        shouldShowCreateBtn() {
            if(this.isUserAdmin && this.companyStatus !== COMPANY.STATUS_PRE_INCORPORATED) 
                return true;
            return false;
        },
    },
    
    methods:{
        view(data) {
            this.$emit('view', data, "view");
        },

        approve(data) {
            if(data.item.isAppointment && ! data.item.appointmentDate) {
                // eslint-disable-next-line no-param-reassign
                data.item.appointmentDate = ' ';
                return;
            }

            if(! data.item.isAppointment && ! data.item.cessationDate) {
                // since the validation provider doesn't triggered inside nested table the below data assignment it required to triggered
                // eslint-disable-next-line no-param-reassign
                data.item.cessationDate = ' ';
                return;
            }

            this.$emit('approve', data, "approve");
        },
    },

    setup(props) {
        const {
            sortByChangeSecretaryForm,
            isBusyChangeSecretaryForm,
            perPageChangeSecretaryForm,
            dataMetaChangeSecretaryForm,
            isLoadingChangeSecretaryForm,
            currentPageChangeSecretaryForm,
            refetchDataChangeSecretaryForm,
            tableColumnsChangeSecretaryForm,
            isSortDirDescChangeSecretaryForm,
            perPageChangeSecretaryFormOptions,
            totalSecretariesChangeSecretaryForm,
            refChangeSecretaryFormTable,
        } = props.payloadForChangeSecretaryFormTable;

        return {
            sortByChangeSecretaryForm,
            perPageChangeSecretaryForm,
            dataMetaChangeSecretaryForm,
            currentPageChangeSecretaryForm,
            tableColumnsChangeSecretaryForm,
            isSortDirDescChangeSecretaryForm,
            perPageChangeSecretaryFormOptions,
            refChangeSecretaryFormTable,

            refetchDataChangeSecretaryForm,
            totalSecretariesChangeSecretaryForm,

            // UI
            isBusyChangeSecretaryForm,
            isLoadingChangeSecretaryForm,
        };
    }
};
</script>

<style lang="scss" scoped>
    .table-responsive {
        overflow-x: inherit;
    }
</style>